import { ORDER_STATUS } from '../../../utils/constant';
import { cache, request } from '../../../utils/index';
import { ExchangeRequest, ExchangeRateResponse, ExchangeOrder } from '../types/Exchange';


interface User {
  _id: string;
  token: string;
}

// ---- fetch exchange rate using params
export const fetchExchangeRate = async (params: {
  fromCurrency: string;
  toCurrency: string;
  fromCurrencyIso2: string;
  toCurrencyIso2: string;
  amount: number;
}): Promise<ExchangeRateResponse> => {
  try {
    const response = await request.get<any>({
      url: '/utility/exchange-rate',
      params: {
        fromCurrency: params.fromCurrency,
        toCurrency: params.toCurrency,
        amount: params.amount,
        fromCurrencyIso2: params.fromCurrency === 'USD' ? 'US' : params.fromCurrencyIso2,
        toCurrencyIso2: params.toCurrency === 'USD' ? 'US' : params.toCurrencyIso2,
      },
    });

    if (!response.success) {
      return {
        rate: 0,
        displayRate: "0",
        fromCurrency: params.fromCurrency,
        toCurrency: params.toCurrency,
        amountToReceive: 0,
        message: response.message || "Exchange rate not available",
        success: false,
        offerId: ''
      };
    }

    const bdcData = response.data;
    return {
      rate: bdcData.rate || 0,
      displayRate: bdcData.toCurrency?.displayRate?.toString() || "1",
      fromCurrency: params.fromCurrency,
      toCurrency: params.toCurrency,
      amountToReceive: bdcData.value || 0,
      success: true,
      offerId: bdcData._id || '',
      paymentDetails: bdcData.paymentDetails,
      acceptedPaymentMethod: bdcData.acceptedPaymentMethod,
      payoutPaymentMethod: bdcData.payoutPaymentMethod,
      allowedMomoProviders: bdcData.allowedMomoProviders,
      allowedCryptoNetworks: bdcData.allowedCryptoNetworks,
    };

  } catch (error) {
    console.error('Error fetching exchange rate:', error);
    throw error;
  }
};

// ---- Create order
export const createExchangeOrder = async (
  offerId: string,
  orderData: ExchangeRequest
): Promise<ExchangeOrder> => {

 const user = cache.get("USER") as User | null;

  if (!user) {
    throw new Error("Session expired. Please sign in again.");
  }

  const userId = user._id;
  const token = user.token;

  const debitCurrency =
  orderData.buyerDebitPaymentDetails?.currency ??
  orderData.buyerDebitPaymentMethod;

  const idempotencyKey = `CREATEORDER::${userId}-${offerId}-${debitCurrency}-${orderData.amount}`;

  try {
    const response = await request.post<any>({
      url: `/offer/${offerId}/order`,
      data: orderData,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'idempotencykey': idempotencyKey,
      },
    });

    if (!response.data) {
      throw new Error("Failed to create exchange order");
    }

    return {
      id: response.data._id || "",
      amount: orderData.amount,
      amountToReceive: response.data.amountToReceive || 0,
      rate: response.data.rate || 0,
      fee: response.data.fee || 0,
      status: response.data.status || ORDER_STATUS.PENDING,
      fromCurrency: response.data.fromCurrency,
      toCurrency: response.data.toCurrency,
      fromCurrencySymbol: "",
      toCurrencySymbol: "", 
      buyerDebitPaymentMethod: orderData.buyerDebitPaymentMethod,
      buyerCreditPaymentMethod: orderData.buyerCreditPaymentMethod,
      bdcPaymentDetails: response.data.sellerCreditPaymentDetails,
    };
  } catch (error) {
    console.error('Error creating exchange order:', error);
    throw error;
  }
};

export const fetchTransactionFee = async (amount: number, iso2: string, type: string): Promise<number> => {
  try {
    const response = await request.get<any>({
      url: '/utility/transaction-fee',
      params: {
        amount: amount,
        iso2: iso2,
        type: type
      },
    });

    return response.data.fee;
  } catch (error) {
    console.error('Error fetching exchange fee:', error);
    throw error;
  }
}